import { getChatCompletion, tools } from '@worktango/ai-assistant/react';
import { fetchAttributeReportQuery } from '~/behaviors/react-query/reporting';
import { fetchSurveyWithSurveyElements } from '~/behaviors/react-query/surveyElements';

const DEFAULT_INSTRUCTIONS = `Identify groups of strength/opportunity, outliers/areas of significantly high/low scores, trends, factors that have a wide spread and great inter-group variability, and factors that have a thin spread and represent a general org-wide sentiment.`;

export const getHeatmapDataTool = tools.createTool({
  name: 'get_heatmap_data',
  description:
    'Get the heatmap data for a survey and attribute. It is a great tool to understand the breakdown of attributes, like which departments have the lowest score.',
  displayName: 'Getting heatmap data',
  pastTenseDisplayName: 'Generated heatmap data',
  parameters: (t) =>
    t.Object({
      surveyId: t.Number({ description: 'The id of the survey to analyze' }),
      attributeId: t.Number({ description: 'The id of the attribute to analyze' }),
      userInstructions: t.Optional(
        t.String({
          description:
            'The instructions provided by the user on how to handle the results. If no instructions are provided, use the default instructions.',
          default: DEFAULT_INSTRUCTIONS,
        }),
      ),
    }),
  fulfill: async (args) => {
    try {
      const surveyId = args.surveyId;
      const attributeId = args.attributeId;
      const instructions = args.userInstructions || DEFAULT_INSTRUCTIONS;

      if (!surveyId) {
        throw new Error('Survey ID is required');
      }

      if (!attributeId) {
        throw new Error('Attribute ID is required');
      }

      const attributeReport = await fetchAttributeReportQuery({
        surveyIds: [surveyId],
        inputAttributeId: attributeId,
        basedOnHierarchy: false,
        requestManagerHierarchies: false,
        selectedAttributes: [],
        selectedTimeframe: null,
      });

      const heatmapAnalysis = await analyseHeatmap({ attributeReport, instructions, surveyId });

      return heatmapAnalysis;
    } catch (error) {
      console.error(error);
      return `Error: ${error}`;
    }
  },
});

async function analyseHeatmap({
  attributeReport,
  instructions,
  surveyId,
}: {
  attributeReport: any;
  instructions: string;
  surveyId: number;
}) {
  const { surveyElements } = await fetchSurveyWithSurveyElements(surveyId);

  const heatmapAnalysis = await getChatCompletion({
    temperature: 0.25,
    model: 'medium',
    stream: false,
    systemInstruction: `
        You are a helpful assistant that analyses the heatmap of a survey.

        <instructions>
        ${instructions}
        </instructions>
        `,
    messages: [
      {
        role: 'user',
        parts: [
          {
            type: 'text',
            text: `Here is the data used to generate the heatmap.
                
                <attributeReport>
                ${JSON.stringify(attributeReport)}
                </attributeReport>

                <questions>
                ${JSON.stringify(surveyElements)}
                </questions>
                `,
          },
        ],
      },
    ],
  });

  if (heatmapAnalysis[0].type !== 'text') {
    throw new Error('The summary returned in an unexpected format. Please try again.');
  }

  return heatmapAnalysis[0].text;
}
